import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowUp, ArrowDown, Truck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/client';

interface MerchantCourier {
  id: number;
  courierId: number;
  priority: number;
  isActive: boolean;
  isDisplay: boolean;
  courier: {
    id: number; name: string; brand?: string; code: string;
    orderType: string; serviceType: string; weightCategory?: string; logoUrl?: string;
  };
}

export function MerchantCouriersSelfTab() {
  const qc = useQueryClient();
  const [typeFilter, setTypeFilter] = useState<'ALL' | 'B2C' | 'B2B'>('ALL');
  const { data, isLoading } = useQuery<MerchantCourier[]>({
    queryKey: ['merchant', 'couriers'],
    queryFn: async () => (await api.get<MerchantCourier[]>('/merchant/couriers')).data,
  });

  const reorder = useMutation({
    mutationFn: async (courierIds: number[]) => (await api.post('/merchant/couriers/reorder', { courierIds })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['merchant', 'couriers'] }); toast.success('Priority updated'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Reorder failed'),
  });

  const move = (idx: number, dir: -1 | 1) => {
    if (!data) return;
    const newOrder = data.map((c) => c.courierId);
    const target = idx + dir;
    if (target < 0 || target >= newOrder.length) return;
    [newOrder[idx], newOrder[target]] = [newOrder[target], newOrder[idx]];
    reorder.mutate(newOrder);
  };

  if (isLoading) {
    return <div className="p-8 text-center text-slate-400"><Loader2 size={20} className="animate-spin inline" /> Loading...</div>;
  }
  if (!data || data.length === 0) {
    return (
      <div className="p-8 text-center text-slate-500">
        <Truck size={32} className="mx-auto mb-3 opacity-40" />
        <div>No couriers assigned to your account yet.</div>
        <p className="text-sm text-slate-400 mt-2">Contact your account manager to enable couriers for your shipments.</p>
      </div>
    );
  }

  const filtered = data.filter((mc) => typeFilter === 'ALL' || mc.courier.orderType === typeFilter);
  const filterActive = typeFilter !== 'ALL';

  return (
    <div>
      <div className="mb-4 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Your assigned couriers</h3>
          <p className="text-sm text-slate-500">Drag priority up or down — lower priority = tried first when booking.</p>
        </div>
        <div className="inline-flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5">
          {(['ALL', 'B2C', 'B2B'] as const).map((t) => (
            <button key={t} onClick={() => setTypeFilter(t)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${typeFilter === t ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-800'}`}>
              {t === 'ALL' ? 'All' : t}
            </button>
          ))}
        </div>
      </div>
      {filtered.length === 0 ? (
        <div className="p-8 text-center text-slate-400 bg-white rounded-xl border border-slate-200">No {typeFilter} couriers assigned.</div>
      ) : (
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-4 py-3 w-16">#</th>
              <th className="text-left px-4 py-3">Courier</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Service</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="px-4 py-3 w-28 text-right">Reorder</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((mc) => {
              const idx = data.findIndex((d) => d.courierId === mc.courierId);
              return (
              <tr key={mc.id} className="border-t border-slate-100">
                <td className="px-4 py-3 tabular-nums text-slate-500">{idx + 1}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {mc.courier.logoUrl && <img src={mc.courier.logoUrl} alt="" className="h-6 w-6 object-contain rounded" />}
                    <div>
                      <div className="font-medium text-slate-900">{mc.courier.name}</div>
                      {mc.courier.brand && <div className="text-xs text-slate-500">{mc.courier.brand}</div>}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-1.5 py-0.5 rounded ${mc.courier.orderType === 'B2C' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                    {mc.courier.orderType}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-600">{mc.courier.serviceType}{mc.courier.weightCategory ? ' · ' + mc.courier.weightCategory : ''}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${mc.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {mc.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => move(idx, -1)} disabled={idx === 0 || reorder.isPending || filterActive}
                      title={filterActive ? 'Clear the filter to reorder' : 'Move up'}
                      className="p-1.5 hover:bg-slate-100 rounded disabled:opacity-30">
                      <ArrowUp size={14} />
                    </button>
                    <button onClick={() => move(idx, 1)} disabled={idx === data.length - 1 || reorder.isPending || filterActive}
                      title={filterActive ? 'Clear the filter to reorder' : 'Move down'}
                      className="p-1.5 hover:bg-slate-100 rounded disabled:opacity-30">
                      <ArrowDown size={14} />
                    </button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}
